import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { AdminUser, ApiEnvelope } from './models';

const tokenKey = 'realty_admin_token';
const adminKey = 'realty_admin_user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokenState = signal<string | null>(localStorage.getItem(tokenKey));
  private readonly adminState = signal<AdminUser | null>(readAdmin());

  readonly token = this.tokenState.asReadonly();
  readonly admin = this.adminState.asReadonly();
  readonly isAuthenticated = computed(() => Boolean(this.tokenState() && this.adminState()));

  login(email: string, password: string): Observable<AdminUser> {
    return this.http
      .post<ApiEnvelope<{ token: string; admin: AdminUser }>>('/api/admin/auth/login', { email, password })
      .pipe(
        map((res) => res.data),
        tap(({ token, admin }) => this.setSession(token, admin)),
        map(({ admin }) => admin)
      );
  }

  logout(): void {
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(adminKey);
    this.tokenState.set(null);
    this.adminState.set(null);
  }

  private setSession(token: string, admin: AdminUser): void {
    localStorage.setItem(tokenKey, token);
    localStorage.setItem(adminKey, JSON.stringify(admin));
    this.tokenState.set(token);
    this.adminState.set(admin);
  }
}

function readAdmin(): AdminUser | null {
  const raw = localStorage.getItem(adminKey);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as AdminUser;
  } catch {
    localStorage.removeItem(adminKey);
    return null;
  }
}
